import React, { useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { LoginForm } from './components/auth/LoginForm';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { Sidebar } from './components/layout/Sidebar';
import { TutorialOverlay } from './components/tutorial/TutorialOverlay';
import { ChatIcon, MenuIcon, MoonIcon, SendIcon, SunIcon } from './components/icons/Icons';
import { GlobalSearch } from './components/search/GlobalSearch';
import { NotificationDropdown } from './components/notifications/NotificationDropdown';
import { NotificationsPage } from './pages/NotificationsPage';
import { ArchivedProjectsPage } from './pages/ArchivedProjectsPage';
import { DashboardPage } from './pages/DashboardPage';
import { WorkspacePage } from './pages/WorkspacePage';
import { MyTasksPage } from './pages/MyTasksPage';
import { CalendarPage } from './pages/CalendarPage';
import { TeamPage } from './pages/TeamPage';
import { ReportsPage } from './pages/ReportsPage';
import { PlanningPage } from './pages/PlanningPage';
import { SettingsPage } from './pages/SettingsPage';
import { MyProfilePage } from './pages/MyProfilePage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { SidebarSection } from './types';
import { useClickOutside } from './hooks/useClickOutside';
import { ToastProvider } from './contexts/ToastContext';
import { Avatar } from './components/common/Avatar';
import { FEATURES } from './config/features';
import './App.css';

/** Route guard — redirects non-PM users to /my-tasks instead of dashboard */
function ProtectedRoute({ section, children }: { section: SidebarSection; children: React.ReactElement }) {
  const { hasSidebarAccess } = useAuth();
  if (!hasSidebarAccess(section)) {
    return <Navigate to="/my-tasks" replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading, isPasswordRecovery } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'var(--surface-0)',
        color: 'var(--text-0)',
        fontSize: '1.2rem',
      }}>
        Loading...
      </div>
    );
  }

  if (isPasswordRecovery) {
    return <ResetPasswordForm />;
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  return (
    <SidebarProvider>
      <ProjectProvider>
        <ToastProvider>
          <MainContent />
        </ToastProvider>
      </ProjectProvider>
    </SidebarProvider>
  );
}

function MainContent() {
  const { isSidebarOpen } = useSidebar();
  const { isProjectManager } = useAuth();
  const [tutorialActive, setTutorialActive] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <>
      <Sidebar />
      <div className={`main-content ${isSidebarOpen ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
        <AppHeader />
        <TutorialOverlay isActive={tutorialActive} onClose={() => setTutorialActive(false)} isProjectManager={isProjectManager} />
        <div className="content">
          <Routes>
            <Route path="/" element={<ProtectedRoute section="dashboard"><DashboardPage /></ProtectedRoute>} />
            <Route path="/workspace" element={<ProtectedRoute section="workspace"><WorkspacePage /></ProtectedRoute>} />
            <Route path="/workspace/:taskId" element={<ProtectedRoute section="workspace"><TaskDetailPage /></ProtectedRoute>} />
            <Route path="/my-tasks" element={<ProtectedRoute section="my-tasks"><MyTasksPage /></ProtectedRoute>} />
            <Route path="/my-tasks/:taskId" element={<ProtectedRoute section="my-tasks"><TaskDetailPage /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute section="calendar"><CalendarPage /></ProtectedRoute>} />
            <Route path="/calendar/:taskId" element={<ProtectedRoute section="calendar"><TaskDetailPage /></ProtectedRoute>} />
            <Route path="/team" element={<ProtectedRoute section="team"><TeamPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute section="reports"><ReportsPage /></ProtectedRoute>} />
            <Route path="/planning" element={<ProtectedRoute section="planning"><PlanningPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute section="settings"><SettingsPage /></ProtectedRoute>} />
            <Route path="/my-profile" element={<MyProfilePage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/archived-projects" element={<ProtectedRoute section="settings"><ArchivedProjectsPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/my-tasks" replace />} />
          </Routes>
        </div>
        {FEATURES.AI_ASSISTANT && (
          <AIChatWidget isOpen={chatOpen} onToggle={() => setChatOpen(prev => !prev)} />
        )}
      </div>
    </>
  );
}

function AIChatWidget({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
  return (
    <div className={`ai-chat-widget ${isOpen ? 'open' : ''}`}>
      {isOpen && (
        <section className="ai-chat-panel" aria-label="AI assistant chat">
          <div className="ai-chat-header">
            <div>
              <h2 className="ai-chat-title">AI Assistant</h2>
              <p className="ai-chat-subtitle">Project support preview</p>
            </div>
            <span className="ai-chat-status">Soon</span>
          </div>
          <div className="ai-chat-messages">
            <div className="ai-chat-message assistant">
              <span className="ai-chat-author">Assistant</span>
              <p>Hi, I will help you search tasks, summarize project status, and answer questions about your schedule.</p>
            </div>
            <div className="ai-chat-message user">
              <span className="ai-chat-author">You</span>
              <p>Show me tasks with issues this week.</p>
            </div>
            <div className="ai-chat-message assistant">
              <span className="ai-chat-author">Assistant</span>
              <p>This is a placeholder conversation. AI responses will be connected in a future release.</p>
            </div>
          </div>
          <form className="ai-chat-input-row">
            <input type="text" placeholder="Ask about a project or task..." aria-label="Ask about a project or task" />
            <button type="button" title="Send message">
              <SendIcon size={16} />
            </button>
          </form>
        </section>
      )}
      <button className="ai-chat-button" type="button" onClick={onToggle} title="Open AI chat">
        <ChatIcon size={24} />
      </button>
    </div>
  );
}

function AppHeader() {
  const { currentUser, logout, hasSidebarAccess } = useAuth();
  const { theme, toggleTheme, isSidebarOpen, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  useClickOutside(profileMenuRef, () => setIsProfileMenuOpen(false), isProfileMenuOpen);

  const displayName = currentUser?.name || '';
  const displayRole = currentUser?.role
    ? currentUser.role
      .toLowerCase()
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
    : '';

  return (
    <header className="app-header">
      <div className="app-header-left">
        <button
          className="header-icon-button menu-button"
          type="button"
          onClick={toggleSidebar}
          title={isSidebarOpen ? 'Close menu' : 'Open menu'}
          aria-label="Toggle navigation menu"
        >
          <MenuIcon size={20} />
        </button>
        <GlobalSearch />
      </div>

      <div className="app-header-right">
        <NotificationDropdown />
        <button
          className="header-theme-toggle"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
        </button>
        <div className="header-user-menu-wrap" ref={profileMenuRef}>
          <button
            className="header-user"
            type="button"
            onClick={() => setIsProfileMenuOpen(prev => !prev)}
            aria-haspopup="menu"
            aria-expanded={isProfileMenuOpen}
          >
            <Avatar user={currentUser} size={46} className="header-user-avatar" />
            <div className="header-user-text">
              <span className="header-user-name">{displayName}</span>
              <span className="header-user-role">{displayRole}</span>
            </div>
          </button>
          {isProfileMenuOpen && (
            <div className="header-profile-menu" role="menu">
              <button
                type="button"
                className="header-profile-menu-item"
                role="menuitem"
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  navigate('/my-profile');
                }}
              >
                My Profile
              </button>
              {hasSidebarAccess('settings') && (
                <button
                  type="button"
                  className="header-profile-menu-item"
                  role="menuitem"
                  onClick={() => {
                    setIsProfileMenuOpen(false);
                    navigate('/settings');
                  }}
                >
                  Settings
                </button>
              )}
              <button
                type="button"
                className="header-profile-menu-item danger"
                role="menuitem"
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  logout();
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default App;
